import { useMemo, useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ship, Anchor, MapPin, CheckCircle2, FileText, Plus, Calendar, AlertTriangle, PackageOpen, Radar } from "lucide-react";
import { useEmbarques, useUpdateEmbarqueEstado, ESTADOS_LOGISTICOS, type Embarque, type EstadoLogistico } from "@/hooks/useEmbarques";
import { useAllDocuments } from "@/hooks/useDocuments";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { CreateEmbarqueModal } from "@/components/CreateEmbarqueModal";
import { EmbarqueDetailModal } from "@/components/EmbarqueDetailModal";
import { cn } from "@/lib/utils";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";

const COLUMN_CONFIG: Record<EstadoLogistico, { icon: typeof Ship; colorClass: string }> = {
  "En Origen": { icon: MapPin, colorClass: "text-amber-600 dark:text-amber-400" },
  "En Tránsito": { icon: Ship, colorClass: "text-blue-600 dark:text-blue-400" },
  "En Puerto": { icon: Anchor, colorClass: "text-purple-600 dark:text-purple-400" },
  "Nacionalizado": { icon: CheckCircle2, colorClass: "text-emerald-600 dark:text-emerald-400" },
};

const REQUIRED_DOC_KEYS = new Set([
  "factura internacional",
  "packing list",
  "bl",
  "declaración de importación",
  "certificado de origen",
]);

function EmbarqueCard({
  embarque,
  onSelect,
  missingDocs,
  isDragging,
}: {
  embarque: Embarque;
  onSelect: (e: Embarque) => void;
  missingDocs: boolean;
  isDragging?: boolean;
}) {
  const updateEstado = useUpdateEmbarqueEstado();

  return (
    <Card
      className={cn(
        "cursor-grab hover:border-primary/50 transition-all bg-card",
        missingDocs && "border-amber-500/60 dark:border-amber-400/40",
        isDragging && "rotate-[2deg] shadow-xl opacity-90 ring-2 ring-primary/40 cursor-grabbing"
      )}
      onClick={() => onSelect(embarque)}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm text-foreground truncate">{embarque.numero_do}</span>
          <div className="flex items-center gap-1.5 shrink-0">
            {embarque.tracking_number && (
              <Radar className="h-3.5 w-3.5 text-emerald-500" />
            )}
            {missingDocs && (
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            )}
            <Badge variant="outline" className="text-xs">
              <FileText className="h-3 w-3 mr-1" />
              {embarque.document_count ?? 0}
            </Badge>
          </div>
        </div>
        {embarque.proveedor && (
          <p className="text-xs text-muted-foreground truncate">{embarque.proveedor}</p>
        )}
        <div className="flex items-center justify-between gap-2">
          {embarque.fecha_llegada_estimada && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(embarque.fecha_llegada_estimada).toLocaleDateString("es-CO", { day: "2-digit", month: "short" })}
            </span>
          )}
          <Select
            value={embarque.estado_logistico}
            onValueChange={(val) => {
              updateEstado.mutate({ id: embarque.id, estado: val as EstadoLogistico });
            }}
          >
            <SelectTrigger
              className="h-7 text-xs w-auto min-w-[100px] ml-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ESTADOS_LOGISTICOS.map((e) => (
                <SelectItem key={e} value={e} className="text-xs">{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

export function KanbanBoard() {
  const { data: embarques = [], isLoading } = useEmbarques();
  const { data: allDocs = [] } = useAllDocuments();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [defaultProvId, setDefaultProvId] = useState<string>();
  const [selectedEmbarque, setSelectedEmbarque] = useState<Embarque | null>(null);
  const queryClient = useQueryClient();
  const updateEstado = useUpdateEmbarqueEstado();
  const canEdit = userRole !== "VISOR";

  // Auto-open create modal from URL params
  useEffect(() => {
    if (searchParams.get("nuevo") === "true") {
      const provId = searchParams.get("proveedorId") || undefined;
      setDefaultProvId(provId);
      setCreateOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const docTypesByEmbarque = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const doc of allDocs) {
      if (!doc.embarque_id) continue;
      if (!map[doc.embarque_id]) map[doc.embarque_id] = new Set();
      const tipo = (doc.tipo_documento ?? "").toLowerCase().trim();
      if (tipo) map[doc.embarque_id].add(tipo);
    }
    return map;
  }, [allDocs]);

  const hasMissingDocs = (embarqueId: string) => {
    const present = docTypesByEmbarque[embarqueId];
    if (!present) return true;
    for (const key of REQUIRED_DOC_KEYS) {
      if (!present.has(key)) return true;
    }
    return false;
  };

  const columns = useMemo(() => {
    const map: Record<EstadoLogistico, Embarque[]> = {
      "En Origen": [],
      "En Tránsito": [],
      "En Puerto": [],
      "Nacionalizado": [],
    };
    for (const e of embarques) {
      const estado = (e.estado_logistico as EstadoLogistico) || "En Origen";
      if (map[estado]) map[estado].push(e);
    }
    return map;
  }, [embarques]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { draggableId, destination, source } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    const newEstado = destination.droppableId as EstadoLogistico;

    // Optimistic update
    queryClient.setQueryData<Embarque[]>(["embarques", undefined], (old) => {
      if (!old) return old;
      return old.map((e) => e.id === draggableId ? { ...e, estado_logistico: newEstado } : e);
    });
    // Also update with user key
    queryClient.setQueriesData<Embarque[]>({ queryKey: ["embarques"] }, (old) => {
      if (!old) return old;
      return old.map((e) => e.id === draggableId ? { ...e, estado_logistico: newEstado } : e);
    });

    updateEstado.mutate({ id: draggableId, estado: newEstado });
  }, [queryClient, updateEstado]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Torre de Control</h2>
        {canEdit && (
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1">
            <Plus className="h-4 w-4" /> Nuevo Embarque
          </Button>
        )}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {ESTADOS_LOGISTICOS.map((estado) => {
            const config = COLUMN_CONFIG[estado];
            const Icon = config.icon;
            const items = columns[estado];

            return (
              <Droppable key={estado} droppableId={estado}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex flex-col rounded-lg border border-border bg-muted/30 min-h-[200px] transition-colors",
                      snapshot.isDraggingOver && "border-primary/50 bg-primary/5"
                    )}
                  >
                    <div className="flex items-center gap-2 p-3 border-b border-border">
                      <Icon className={cn("h-4 w-4", config.colorClass)} />
                      <span className="text-sm font-medium text-foreground">{estado}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">{items.length}</Badge>
                    </div>
                    <div className="flex-1 p-2 space-y-2 overflow-auto max-h-[60vh]">
                      {isLoading ? (
                        <>
                          <Skeleton className="h-24 w-full" />
                          <Skeleton className="h-24 w-full" />
                        </>
                      ) : items.length === 0 && !snapshot.isDraggingOver ? (
                        <div className="flex flex-col items-center justify-center py-10 px-4 border-2 border-dashed border-border rounded-lg bg-muted/20">
                          <PackageOpen className="h-8 w-8 text-muted-foreground/40 mb-2" />
                          <p className="text-xs text-muted-foreground text-center">Sin embarques</p>
                        </div>
                      ) : (
                        items.map((e, index) => (
                          <Draggable key={e.id} draggableId={e.id} index={index} isDragDisabled={!canEdit}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                <EmbarqueCard
                                  embarque={e}
                                  onSelect={setSelectedEmbarque}
                                  missingDocs={hasMissingDocs(e.id)}
                                  isDragging={dragSnapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      <CreateEmbarqueModal open={createOpen} onOpenChange={setCreateOpen} defaultProveedorId={defaultProvId} />
      <EmbarqueDetailModal embarque={selectedEmbarque} onClose={() => setSelectedEmbarque(null)} />
    </div>
  );
}
